/**
 * Sala ao vivo: o movimento do dia chegando (app do beneficiário, reembolsos,
 * Kanban, WhatsApp e URA), para acompanhar numa TV ou no computador.
 *
 * Como o "ao vivo" funciona: a tela lê o estado do dia a cada 30 s e os eventos
 * novos a cada 15 s. Os eventos de cada leitura entram um por um, espalhados no
 * intervalo, para o olho acompanhar. Nada se mexe sem dado novo; cada evento
 * mostra a hora real em que aconteceu. A reprise toca as últimas 24 h aceleradas
 * e fica marcada como reprise.
 * Ninguém aparece pelo nome (é tela de sala): tipo, hora, cidade, idade e perfil.
 * Backend: GET /sala-ao-vivo/estado e /sala-ao-vivo/eventos (sala_ao_vivo.py).
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import InfoTip from "../components/InfoTip";

const C = {
  bg: "#07131a", painel: "#0c1e27", linha: "#16323d", texto: "#d9ecef", apagado: "#7f9ca3",
  acento: "#2fd3b4", azul: "#5aa9ff", ambar: "#f2b35b", ambarClaro: "#f6d28f", coral: "#ff7a6b", lilas: "#b892ff",
};
const F = {
  display: "'Saira Semi Condensed', 'Arial Narrow', sans-serif",
  corpo: "'Manrope', 'Segoe UI', system-ui, sans-serif",
  dado: "'IBM Plex Mono', ui-monospace, Consolas, monospace",
};
const TIPOS = {
  acesso: { cor: C.acento, rotulo: "Acesso ao app", frase: () => "Acesso ao app" },
  cadastro: { cor: C.lilas, rotulo: "Cadastro no app", frase: () => "Primeiro cadastro no app" },
  reembolso_app: { cor: C.ambar, rotulo: "Reembolso pelo app", frase: () => "Reembolso enviado pelo app" },
  reembolso_erp: { cor: C.ambarClaro, rotulo: "Pedido de reembolso", frase: () => "Pedido de reembolso registrado" },
  autorizacao: { cor: "#8fd3ff", rotulo: "Autorização", frase: (e) => `Autorização emitida${e.detalhe ? `: ${e.detalhe}` : ""}` },
  atendimento: { cor: C.azul, rotulo: "Atendimento aberto", frase: (e) => `Atendimento aberto${e.detalhe ? ` (${e.detalhe})` : ""}` },
  whatsapp: { cor: C.coral, rotulo: "WhatsApp recebido", frase: () => "Mensagem de WhatsApp recebida" },
  ligacao: { cor: "#ff9f8f", rotulo: "Ligação na URA", frase: (e) => `Ligação na URA${e.detalhe ? `, fila ${e.detalhe}` : ""}` },
};
// "Pedidos de reembolso" abre a linha de produção do reembolso (a análise é lá).
const KPIS = [
  { k: ["acesso"], rotulo: "Acessos ao app", cor: C.acento },
  { k: ["cadastro"], rotulo: "Cadastros no app", cor: C.lilas },
  { k: ["reembolso_erp", "reembolso_app"], rotulo: "Pedidos de reembolso", cor: C.ambar, sub: "reembolso_app", subRot: "pelo app", link: "/producao/reembolso?origem=sala" },
  { k: ["autorizacao"], rotulo: "Autorizações", cor: "#8fd3ff", subFixo: "lotes às 9h e 13h" },
  { k: ["atendimento"], rotulo: "Atendimentos abertos", cor: C.azul },
  { k: ["ligacao", "whatsapp"], rotulo: "Ligações e WhatsApp", cor: C.coral },
];
const TIP = "O que está acontecendo hoje, lido direto do app do beneficiário e do AURA, sem esperar sincronização. "
  + "Os números são de hoje (horário de Brasília); a comparação é com a média do mesmo dia da semana nas últimas semanas com registro, até esta mesma hora. "
  + "Pedido de reembolso é a primeira vez que o pedido aparece no ERP, de qualquer canal; a situação dele (aprovado, pago) ainda não é mostrada. "
  + "Autorizações são as guias emitidas no ERP, lidas em dois lotes por dia (9h e 13h): cada lote entra na tela um por um, com a hora real da emissão, o tipo de guia e a rede. "
  + "Ninguém aparece pelo nome. O acompanhamento de reembolso é lido a cada 30 minutos, então esses pedidos chegam em lotes e entram na tela um por um. Clique em Pedidos de reembolso para abrir a linha de produção.";
const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("pt-BR"));
// Horário de Brasília em qualquer computador ou TV (o dia da sala é o de Brasília).
const FUSO = "America/Sao_Paulo";
const hora = (iso) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: FUSO });
const horaDoDia = (iso) => Number(new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", hourCycle: "h23", timeZone: FUSO }).format(new Date(iso)));
const detalhe = (e) => [e.perfil, e.idade != null ? `${e.idade} anos` : null, e.cidade].filter(Boolean).join(", ") || "sem cadastro no AURA";
const hexA = (hex, a) => { const n = parseInt(hex.slice(1), 16); return `rgba(${n >> 16 & 255},${n >> 8 & 255},${n & 255},${Math.max(0, a)})`; };
const bez = (a, m, b, t) => (1 - t) * (1 - t) * a + 2 * (1 - t) * t * m + t * t * b;

function Numero({ valor }) {
  const [anim, setAnim] = useState(false);
  const ant = useRef(valor);
  useEffect(() => {
    if (valor !== ant.current) { setAnim(true); const t = setTimeout(() => setAnim(false), 450); ant.current = valor; return () => clearTimeout(t); }
    return undefined;
  }, [valor]);
  return <span style={{ display: "inline-block", transition: "transform .35s cubic-bezier(.2,.9,.3,1.3), color .35s", transform: anim ? "translateY(-12%)" : "none", color: anim ? C.acento : C.texto }}>{fmt(valor)}</span>;
}

export default function SalaAoVivo() {
  const navigate = useNavigate();
  const raiz = useRef(null);
  const [prorrog, setProrrog] = useState(null);
  useEffect(() => { api.get("/radar-carteira/prorrogacoes", { params: { estado: "abertos" } }).then((r) => setProrrog(r.data.resumo)).catch(() => {}); }, []);
  const cvRef = useRef(null), anelRef = useRef(null), curvaRef = useRef(null);
  const [estado, setEstado] = useState(null);
  const [cont, setCont] = useState(null);
  const [feed, setFeed] = useState([]);
  const [modo, setModo] = useState("vivo");          // vivo | reprise
  const [reprisePos, setReprisePos] = useState(null);
  const [pausado, setPausado] = useState(false);
  const [erro, setErro] = useState(null);
  const [relogio, setRelogio] = useState(new Date());
  const [ultimoNovo, setUltimoNovo] = useState(Date.now());
  const sim = useRef({ fila: [], vistos: new Set(), cursor: null, nos: [], viagens: [], faiscas: [], ondas: [], poeira: [], nucleo: 0, anel: 0, curvaPulso: 0,
                       curva: null, cidades: [], adocao: null, total: 0, W: 0, H: 0 });
  const pausaRef = useRef(false); pausaRef.current = pausado;
  const modoRef = useRef(modo); modoRef.current = modo;

  // relógio
  useEffect(() => { const t = setInterval(() => setRelogio(new Date()), 1000); return () => clearInterval(t); }, []);

  // estado do dia (30 s)
  useEffect(() => {
    let vivo = true;
    const ler = () => api.get("/sala-ao-vivo/estado").then((r) => {
      if (!vivo) return;
      const d = r.data; setEstado(d); setErro(null);
      const s = sim.current;
      if (!s.cursor) {   // primeira leitura: contadores, feed e cursor
        setCont({ ...d.contadores });
        setFeed([...d.ultimos].reverse().map((e) => ({ ...e, novo: false })));
        d.ultimos.forEach((e) => s.vistos.add(e.id));
        s.cursor = d.agora;
        s.total = Object.values(d.contadores).reduce((a, b) => a + b, 0);
      }
      s.curva = { hoje: [...d.curva.hoje], passada: d.curva.semana_passada };
      s.adocao = d.adocao;
      montaNos(d.cidades);
    }).catch((e) => vivo && setErro(e?.response?.data?.detail || "Não foi possível ler o movimento do dia."));
    ler();
    const t = setInterval(ler, 30000);
    return () => { vivo = false; clearInterval(t); };
  }, []);

  // eventos novos (15 s), espalhados no intervalo
  useEffect(() => {
    let vivo = true;
    const buscar = () => {
      const s = sim.current;
      if (!s.cursor || modoRef.current !== "vivo") return;
      api.get("/sala-ao-vivo/eventos", { params: { desde: s.cursor, limite: 300 } }).then((r) => {
        if (!vivo) return;
        s.cursor = r.data.ate;
        const novos = r.data.eventos.filter((e) => !s.vistos.has(e.id));
        novos.forEach((e, i) => { s.vistos.add(e.id); s.fila.push({ e, quando: performance.now() + (i / Math.max(1, novos.length)) * 13000 }); });
        if (novos.length) setUltimoNovo(Date.now());
      }).catch(() => {});
    };
    const t = setInterval(buscar, 15000);
    return () => { vivo = false; clearInterval(t); };
  }, []);

  // reprise das últimas 24 h, acelerada (24 h em ~8 min)
  const iniciarReprise = () => {
    const s = sim.current;
    const ate = new Date(), desde = new Date(ate.getTime() - 24 * 3600 * 1000);
    api.get("/sala-ao-vivo/eventos", { params: { desde: desde.toISOString(), ate: ate.toISOString(), limite: 3000 } }).then((r) => {
      const ev = r.data.eventos;
      s.fila = [];
      const escala = (8 * 60 * 1000) / (24 * 3600 * 1000);
      let quando = performance.now() + 400, anterior = null;
      ev.forEach((e) => {
        const t = new Date(e.t).getTime();
        if (anterior !== null) quando += Math.min(2500, Math.max(120, (t - anterior) * escala));
        anterior = t;
        s.fila.push({ e, quando, reprise: true });
      });
      setModo("reprise"); setReprisePos({ total: ev.length, feitos: 0 });
      setFeed([]); setCont(Object.fromEntries(Object.keys(TIPOS).map((k) => [k, 0]))); s.total = 0;
    });
  };
  const voltarAoVivo = () => {
    const s = sim.current; s.fila = [];
    setModo("vivo"); setReprisePos(null);
    if (estado) { setCont({ ...estado.contadores }); s.total = Object.values(estado.contadores).reduce((a, b) => a + b, 0);
      setFeed([...estado.ultimos].reverse().map((e) => ({ ...e, novo: false }))); }
  };

  function montaNos(cidades) {
    const s = sim.current; s.cidades = cidades || s.cidades || []; cidades = s.cidades;
    const { W, H } = s; if (!W) return;
    const lista = [...cidades.map((c) => c.cidade), "Outras cidades"];
    const cx = W / 2, cy = H / 2 + 6, rx = Math.min(W * .42, 430), ry = Math.min(H * .38, 235);
    const antigos = Object.fromEntries(s.nos.map((n) => [n.nome, n]));
    s.nos = lista.map((nome, i) => {
      const a = -Math.PI / 2 + (i / lista.length) * Math.PI * 2;
      const c = cidades.find((x) => x.cidade === nome);
      return { nome, x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * ry, brilho: antigos[nome]?.brilho || 0,
               total: c ? c.n : (antigos[nome]?.total || 0), peso: c ? c.n : 1 };
    });
  }

  // canvas: núcleo, viagens, anel e curva
  useEffect(() => {
    const cv = cvRef.current, ctx = cv.getContext("2d"), s = sim.current;
    const reduz = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const layout = () => {
      const dpr = window.devicePixelRatio || 1; s.W = cv.clientWidth; s.H = cv.clientHeight;
      cv.width = Math.round(s.W * dpr); cv.height = Math.round(s.H * dpr); ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      s.poeira = Array.from({ length: Math.round(s.W * s.H / 2600) }, () => ({ a: Math.random() * Math.PI * 2, r: 30 + Math.random() * 90, v: (Math.random() - .5) * .004, sz: Math.random() * 1.4 + .3 }));
      montaNos(s.cidades);
    };
    const obs = new ResizeObserver(layout); obs.observe(cv); layout();

    const lanca = (item) => {
      const e = item.e;
      let no = s.nos.find((n) => n.nome === e.cidade) || s.nos.find((n) => n.nome === "Outras cidades");
      if (!no) return chega(item);
      no.brilho = 1;
      const cx = s.W / 2, cy = s.H / 2 + 6;
      s.viagens.push({ item, x0: no.x, y0: no.y, mx: (no.x + cx) / 2 + (Math.random() - .5) * 120, my: (no.y + cy) / 2 + (Math.random() - .5) * 90,
                       x1: cx, y1: cy, t: 0, dur: reduz ? 1 : 1300 + Math.random() * 700, cor: TIPOS[e.tipo]?.cor || C.acento, rastro: [], no });
    };
    const chega = (item) => {
      const e = item.e, cor = TIPOS[e.tipo]?.cor || C.acento;
      s.nucleo = 1; s.ondas.push({ r: 22, a: .7, cor });
      for (let i = 0; i < 14; i++) { const a = Math.random() * Math.PI * 2, v = 1 + Math.random() * 2.6; s.faiscas.push({ x: s.W / 2, y: s.H / 2 + 6, vx: Math.cos(a) * v, vy: Math.sin(a) * v, vida: 1, cor }); }
      s.total++;
      setCont((c) => (c ? { ...c, [e.tipo]: (c[e.tipo] || 0) + 1 } : c));
      setFeed((f) => [{ ...e, novo: true }, ...f.map((x) => ({ ...x, novo: false }))].slice(0, 14));
      if (e.tipo === "cadastro") s.anel = 1;
      if (!item.reprise && s.curva) { const h = horaDoDia(e.t); s.curva.hoje[h] = (s.curva.hoje[h] || 0) + 1; s.curvaPulso = 1; }
      if (item.reprise) setReprisePos((p) => (p ? { ...p, feitos: p.feitos + 1 } : p));
    };

    let ultimo = performance.now(), raf;
    // Um quadro que falha não pode parar a sala: o próximo é agendado mesmo assim.
    const quadro = (agora) => {
      try { desenha(agora); } catch (err) { if (!s.avisou) { console.error("sala ao vivo:", err); s.avisou = true; } }
      raf = requestAnimationFrame(quadro);
    };
    const desenha = (agora) => {
      const dt = Math.min(64, agora - ultimo); ultimo = agora;
      if (!pausaRef.current) {
        while (s.fila.length && s.fila[0].quando <= agora) lanca(s.fila.shift());
      } else {
        s.fila.forEach((f) => { f.quando += dt; });
      }
      const W = s.W, H = s.H, cx = W / 2, cy = H / 2 + 6;
      ctx.clearRect(0, 0, W, H);
      s.poeira.forEach((p) => { p.a += p.v * dt; ctx.fillStyle = `rgba(47,211,180,${.10 + p.sz * .08})`; ctx.fillRect(cx + Math.cos(p.a) * p.r * 1.6, cy + Math.sin(p.a) * p.r * .8, p.sz, p.sz); });
      ctx.lineWidth = 1;
      s.nos.forEach((n) => { ctx.strokeStyle = `rgba(127,156,163,${.08 + n.brilho * .25})`; ctx.beginPath(); ctx.moveTo(n.x, n.y); ctx.lineTo(cx, cy); ctx.stroke(); });
      s.nucleo = Math.max(0, s.nucleo - dt / 900);
      const rN = 36 + s.nucleo * 8;
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rN * 2.4); g.addColorStop(0, `rgba(47,211,180,${.55 + s.nucleo * .4})`); g.addColorStop(.35, "rgba(47,211,180,.16)"); g.addColorStop(1, "rgba(47,211,180,0)");
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, rN * 2.4, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = C.acento; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(cx, cy, rN, 0, Math.PI * 2); ctx.stroke();
      ctx.textAlign = "center"; ctx.fillStyle = C.texto; ctx.font = `600 20px ${F.display}`; ctx.fillText(fmt(s.total), cx, cy + 3);
      ctx.font = `10px ${F.dado}`; ctx.fillStyle = C.apagado; ctx.fillText(modoRef.current === "reprise" ? "na reprise" : "eventos hoje", cx, cy + 17);
      s.ondas = s.ondas.filter((o) => (o.a -= dt / 1300) > 0); s.ondas.forEach((o) => { o.r += dt * .09; ctx.strokeStyle = hexA(o.cor, o.a); ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(cx, cy, o.r, 0, Math.PI * 2); ctx.stroke(); });
      s.viagens = s.viagens.filter((v) => {
        v.t += dt / v.dur; const t = Math.min(1, v.t), e = t < .5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        const x = bez(v.x0, v.mx, v.x1, e), y = bez(v.y0, v.my, v.y1, e);
        v.rastro.push([x, y]); if (v.rastro.length > 22) v.rastro.shift();
        v.rastro.forEach(([a, b], i) => { ctx.fillStyle = hexA(v.cor, (i / v.rastro.length) * .6); ctx.beginPath(); ctx.arc(a, b, 1 + i / v.rastro.length * 2.2, 0, Math.PI * 2); ctx.fill(); });
        ctx.fillStyle = v.cor; ctx.shadowColor = v.cor; ctx.shadowBlur = 14; ctx.beginPath(); ctx.arc(x, y, 3.6, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0;
        if (t >= 1) { v.no.total = (v.no.total || 0) + (modoRef.current === "vivo" ? 1 : 0); chega(v.item); return false; }
        return true;
      });
      s.faiscas = s.faiscas.filter((f) => (f.vida -= dt / 700) > 0);
      s.faiscas.forEach((f) => { f.x += f.vx * dt / 16; f.y += f.vy * dt / 16; f.vx *= .96; f.vy *= .96; ctx.fillStyle = hexA(f.cor, f.vida); ctx.fillRect(f.x, f.y, 2, 2); });
      s.nos.forEach((n) => {
        n.brilho = Math.max(0, n.brilho - dt / 1200);
        const r = 3 + Math.min(9, Math.sqrt(n.peso) * 1.3);
        if (n.brilho > 0) { ctx.strokeStyle = `rgba(47,211,180,${n.brilho * .8})`; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(n.x, n.y, r + (1 - n.brilho) * 18, 0, Math.PI * 2); ctx.stroke(); }
        ctx.fillStyle = n.brilho > .05 ? C.acento : "rgba(217,236,239,.75)"; ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, Math.PI * 2); ctx.fill();
        const abaixo = n.y > cy; ctx.font = `11px ${F.corpo}`; ctx.fillStyle = n.brilho > .05 ? C.texto : C.apagado;
        ctx.fillText(n.nome, n.x, n.y + (abaixo ? r + 13 : -r - 6));
        ctx.font = `10px ${F.dado}`; ctx.fillStyle = C.apagado; ctx.fillText(fmt(n.total), n.x, n.y + (abaixo ? r + 25 : -r - 18));
      });
      desenhaAnel(); desenhaCurva(dt);
    };
    const desenhaAnel = () => {
      const el = anelRef.current; if (!el || el.width < 64) return; const a = el.getContext("2d"), w = el.width, c = w / 2, r = c - 16;
      s.anel = Math.max(0, s.anel - 1 / 60);
      const p = s.adocao?.pct ? s.adocao.pct / 100 : 0;
      a.clearRect(0, 0, w, w); a.lineWidth = 14; a.lineCap = "round";
      a.strokeStyle = C.linha; a.beginPath(); a.arc(c, c, r, 0, Math.PI * 2); a.stroke();
      const gr = a.createLinearGradient(0, 0, w, w); gr.addColorStop(0, C.acento); gr.addColorStop(1, C.lilas);
      a.strokeStyle = gr; a.shadowColor = C.acento; a.shadowBlur = 8 + s.anel * 26;
      a.beginPath(); a.arc(c, c, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.max(p, .004)); a.stroke(); a.shadowBlur = 0;
    };
    const desenhaCurva = (dt) => {
      const el = curvaRef.current; if (!el || !s.curva) return;
      const a = el.getContext("2d"), dpr = window.devicePixelRatio || 1, W = el.clientWidth, H = el.clientHeight;
      if (!W || !H) return;
      if (el.width !== Math.round(W * dpr) || el.height !== Math.round(H * dpr)) { el.width = Math.round(W * dpr); el.height = Math.round(H * dpr); }
      a.setTransform(dpr, 0, 0, dpr, 0, 0); a.clearRect(0, 0, W, H);
      s.curvaPulso = Math.max(0, s.curvaPulso - dt / 900);
      const hoje = s.curva.hoje, pass = s.curva.passada;
      const max = Math.max(4, ...pass, ...hoje.filter((v) => v != null)) * 1.15;
      const pad = { l: 28, r: 8, t: 6, b: 16 }, x = (h) => pad.l + (W - pad.l - pad.r) * h / 23, y = (v) => H - pad.b - (H - pad.t - pad.b) * v / max;
      a.font = `10px ${F.dado}`; a.fillStyle = C.apagado; a.strokeStyle = C.linha; a.lineWidth = 1;
      [0, .5, 1].forEach((f) => { const v = Math.round(max * f / 1.15); a.beginPath(); a.moveTo(pad.l, y(v)); a.lineTo(W - pad.r, y(v)); a.stroke(); a.fillText(v, 2, y(v) + 3); });
      [0, 6, 12, 18, 23].forEach((h) => a.fillText(`${h}h`, x(h) - 8, H - 3));
      a.strokeStyle = C.apagado; a.globalAlpha = .55; a.setLineDash([3, 3]); a.beginPath(); pass.forEach((v, h) => (h ? a.lineTo(x(h), y(v)) : a.moveTo(x(h), y(v)))); a.stroke(); a.setLineDash([]); a.globalAlpha = 1;
      const pts = hoje.map((v, h) => (v == null ? null : [x(h), y(v)])).filter(Boolean); if (!pts.length) return;
      const gr = a.createLinearGradient(0, pad.t, 0, H - pad.b); gr.addColorStop(0, "rgba(47,211,180,.35)"); gr.addColorStop(1, "rgba(47,211,180,0)");
      a.beginPath(); pts.forEach(([p, q], i) => (i ? a.lineTo(p, q) : a.moveTo(p, q))); a.lineTo(pts[pts.length - 1][0], H - pad.b); a.lineTo(pts[0][0], H - pad.b); a.closePath(); a.fillStyle = gr; a.fill();
      a.beginPath(); pts.forEach(([p, q], i) => (i ? a.lineTo(p, q) : a.moveTo(p, q))); a.strokeStyle = C.acento; a.lineWidth = 2; a.stroke();
      const [ex, ey] = pts[pts.length - 1]; a.fillStyle = C.acento; a.beginPath(); a.arc(ex, ey, 3.5, 0, Math.PI * 2); a.fill();
      if (s.curvaPulso > 0) { a.strokeStyle = `rgba(47,211,180,${s.curvaPulso})`; a.lineWidth = 2; a.beginPath(); a.arc(ex, ey, 4 + (1 - s.curvaPulso) * 14, 0, Math.PI * 2); a.stroke(); }
    };
    raf = requestAnimationFrame(quadro);
    return () => { cancelAnimationFrame(raf); obs.disconnect(); };
  }, []);

  useEffect(() => { if (estado) montaNos(estado.cidades); }, [estado]);

  const telaCheia = () => {
    const el = raiz.current;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); else el?.requestFullscreen?.().catch(() => {});
  };
  const comp = (k) => {
    if (!estado || !cont) return null;
    const hoje = k.k.reduce((s, t) => s + (cont[t] || 0), 0);
    const medias = k.k.map((t) => estado.media_ate_agora[t]).filter((v) => v != null);
    if (modo === "reprise") return "na reprise";
    if (!medias.length) return "sem histórico para comparar";
    const m = medias.reduce((a, b) => a + b, 0);
    if (!m) return "média zero até esta hora";
    return `${Math.round(100 * hoje / m)}% da média até esta hora`;
  };
  const quieto = modo === "vivo" && Date.now() - ultimoNovo > 10 * 60 * 1000;
  const adocao = estado?.adocao;

  return (
    <div ref={raiz} style={{ background: `radial-gradient(1200px 600px at 50% 45%, rgba(47,211,180,.07), transparent 60%), ${C.bg}`, color: C.texto, fontFamily: F.corpo,
                             minHeight: "100%", padding: "14px 18px", display: "grid", gridTemplateRows: "auto auto 1fr auto", gap: 12, boxSizing: "border-box", overflow: "auto" }}>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Saira+Semi+Condensed:wght@400;600;700&family=Manrope:wght@400;500;700&family=IBM+Plex+Mono:wght@400;500&display=swap" />
      <style>{`
        @keyframes salaPulso { 0% { box-shadow: 0 0 0 0 rgba(47,211,180,.55); } 100% { box-shadow: 0 0 0 10px rgba(47,211,180,0); } }
        @keyframes salaEntra { from { opacity: 0; transform: translateY(-14px) scale(.98); } to { opacity: 1; transform: none; } }
        .sala-btn { font: 600 12px ${F.corpo}; color: ${C.texto}; background: transparent; border: 1px solid ${C.linha}; border-radius: 6px; padding: 6px 12px; cursor: pointer; }
        .sala-btn:hover, .sala-btn:focus-visible { border-color: ${C.acento}; color: ${C.acento}; outline: none; }
        .sala-feed li { animation: salaEntra .6s cubic-bezier(.2,.9,.3,1) both; }
        @media (prefers-reduced-motion: reduce) { .sala-feed li, .sala-vivo i { animation: none !important; } }
        @media (max-width: 1100px) { .sala-centro { grid-template-columns: 1fr !important; height: auto !important; } .sala-centro > section { height: auto !important; } .sala-nucleo { min-height: 380px !important; } .sala-feed { max-height: 360px; } }
      `}</style>

      <header style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px 18px" }}>
        <div style={{ fontFamily: F.display, fontWeight: 700, fontSize: 22, letterSpacing: ".08em", textTransform: "uppercase" }}>AURA <span style={{ color: C.acento }}>·</span> Sala ao vivo</div>
        {modo === "vivo" ? (
          <div className="sala-vivo" style={{ display: "inline-flex", alignItems: "center", gap: 7, fontFamily: F.dado, fontSize: 12, color: C.acento, letterSpacing: ".12em" }}>
            <i style={{ width: 8, height: 8, borderRadius: "50%", background: C.acento, animation: "salaPulso 1.6s infinite" }} />AO VIVO
          </div>
        ) : (
          <div style={{ fontFamily: F.dado, fontSize: 12, color: C.ambar, letterSpacing: ".1em", border: `1px solid ${C.ambar}66`, padding: "3px 8px", borderRadius: 4 }}>
            REPRISE DAS ÚLTIMAS 24 H, ACELERADA{reprisePos ? ` · ${fmt(reprisePos.feitos)} de ${fmt(reprisePos.total)}` : ""}
          </div>
        )}
        <InfoTip w={420} texto={TIP} cor={C.apagado} />
        {prorrog && (
          <button className="sala-btn" type="button" onClick={() => navigate("/internacoes-prorrogadas")}
                  style={prorrog.internacoes > 0 ? { borderColor: C.coral, color: C.coral } : {}}>
            {prorrog.internacoes > 0
              ? <>Internações prorrogadas na semana: {prorrog.internacoes} ({prorrog.muitas} com 3 ou mais) ›</>
              : <>Internações prorrogadas na semana: nenhuma até agora ›</>}
          </button>
        )}
        <div style={{ flex: 1 }} />
        <div style={{ fontFamily: F.dado, fontSize: 15, color: C.apagado, fontVariantNumeric: "tabular-nums" }}>{relogio.toLocaleTimeString("pt-BR", { timeZone: FUSO })}</div>
        {modo === "vivo"
          ? <button className="sala-btn" type="button" onClick={iniciarReprise}>Reprise das últimas 24 h</button>
          : <button className="sala-btn" type="button" onClick={voltarAoVivo}>Voltar ao vivo</button>}
        <button className="sala-btn" type="button" onClick={telaCheia}>Tela cheia</button>
        <button className="sala-btn" type="button" onClick={() => setPausado((p) => !p)}>{pausado ? "Continuar" : "Pausar"}</button>
      </header>

      {erro && <div style={{ color: C.coral, fontSize: 13 }}>{erro}</div>}

      <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }} aria-label="Contadores de hoje">
        {KPIS.map((k) => (
          <div key={k.rotulo} role={k.link ? "link" : undefined} tabIndex={k.link ? 0 : undefined}
               onClick={k.link ? () => navigate(k.link) : undefined} onKeyDown={k.link ? (e) => e.key === "Enter" && navigate(k.link) : undefined}
               title={k.link ? "Abrir a análise" : undefined}
               style={{ background: C.painel, border: `1px solid ${C.linha}`, borderRadius: 8, padding: "10px 14px", minWidth: 0, cursor: k.link ? "pointer" : "default" }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".1em", color: C.apagado, display: "flex", alignItems: "center", gap: 6 }}>
              <b style={{ width: 8, height: 8, borderRadius: 2, background: k.cor, display: "inline-block" }} />{k.rotulo}
            </div>
            <div style={{ fontFamily: F.display, fontWeight: 600, fontSize: 34, lineHeight: 1.1, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>
              <Numero valor={cont ? k.k.reduce((s, t) => s + (cont[t] || 0), 0) : null} />
            </div>
            <div style={{ fontFamily: F.dado, fontSize: 11, color: C.apagado }}>
              {comp(k)}{k.sub && cont ? ` · ${fmt(cont[k.sub] || 0)} ${k.subRot}` : ""}{k.subFixo ? ` · ${k.subFixo}` : ""}{k.link ? " · abrir ›" : ""}
            </div>
          </div>
        ))}
      </section>

      <main className="sala-centro" style={{ display: "grid", gridTemplateColumns: "minmax(250px, 1fr) minmax(0, 2.2fr) minmax(260px, 1fr)", gap: 12, minHeight: 0,
                                              height: "max(460px, calc(100vh - 300px))" }}>
        <section style={{ background: C.painel, border: `1px solid ${C.linha}`, borderRadius: 8, padding: "12px 14px", minWidth: 0, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", boxSizing: "border-box" }} aria-label="Últimos eventos">
          <h2 style={{ margin: "0 0 8px", font: `600 12px ${F.corpo}`, letterSpacing: ".12em", textTransform: "uppercase", color: C.apagado }}>Últimos eventos</h2>
          {quieto && <div style={{ fontSize: 12, color: C.apagado, marginBottom: 6 }}>Sem eventos novos há mais de 10 minutos. A reprise mostra como foram as últimas 24 h.</div>}
          <ul className="sala-feed" style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6, overflow: "hidden", flex: 1, minHeight: 0 }}>
            {feed.map((e) => (
              <li key={e.id} style={{ display: "grid", gridTemplateColumns: "8px 1fr auto", gap: 8, alignItems: "start", padding: "7px 8px", borderRadius: 6,
                                       background: e.novo ? "rgba(47,211,180,.07)" : "rgba(255,255,255,.02)", border: `1px solid ${e.novo ? "rgba(47,211,180,.35)" : "transparent"}` }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", marginTop: 5, background: TIPOS[e.tipo]?.cor }} />
                <span style={{ fontSize: 13, lineHeight: 1.35, minWidth: 0 }}>{TIPOS[e.tipo]?.frase(e) || e.tipo}<small style={{ display: "block", color: C.apagado, fontSize: 11.5 }}>{detalhe(e)}</small></span>
                <time style={{ fontFamily: F.dado, fontSize: 11, color: C.apagado, whiteSpace: "nowrap" }}>{hora(e.t)}</time>
              </li>
            ))}
            {!feed.length && <li style={{ fontSize: 13, color: C.apagado }}>{estado ? "Nenhum evento ainda hoje." : "Lendo o movimento do dia…"}</li>}
          </ul>
        </section>

        <section className="sala-nucleo" style={{ background: C.painel, border: `1px solid ${C.linha}`, borderRadius: 8, position: "relative", overflow: "hidden", height: "100%", minHeight: 0 }} aria-label="Movimento por cidade">
          <div style={{ position: "absolute", left: 14, top: 10, font: `600 12px ${F.corpo}`, letterSpacing: ".12em", textTransform: "uppercase", color: C.apagado }}>Movimento por cidade, hoje</div>
          <canvas ref={cvRef} style={{ width: "100%", height: "100%", display: "block", position: "absolute", inset: 0 }} />
          <div style={{ position: "absolute", left: 12, bottom: 10, display: "flex", flexWrap: "wrap", gap: "6px 12px", fontSize: 11.5, color: C.apagado }}>
            {Object.entries(TIPOS).map(([k, v]) => <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><i style={{ width: 9, height: 9, borderRadius: "50%", background: v.cor, display: "inline-block" }} />{v.rotulo}</span>)}
          </div>
        </section>

        <section style={{ background: C.painel, border: `1px solid ${C.linha}`, borderRadius: 8, padding: "12px 14px", minWidth: 0, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", boxSizing: "border-box" }} aria-label="Adoção do app e curva do dia">
          <h2 style={{ margin: "0 0 8px", font: `600 12px ${F.corpo}`, letterSpacing: ".12em", textTransform: "uppercase", color: C.apagado }}>Adoção do app</h2>
          {adocao ? (
            <div style={{ display: "grid", gridTemplateColumns: "116px 1fr", gap: 12, alignItems: "center" }}>
              <canvas ref={anelRef} width={232} height={232} style={{ width: 116, height: 116 }} />
              <div>
                <div style={{ fontFamily: F.display, fontWeight: 700, fontSize: 32, color: C.acento, fontVariantNumeric: "tabular-nums" }}>
                  {adocao.pct != null ? `${adocao.pct.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%` : "—"}
                </div>
                <p style={{ margin: "2px 0 0", fontSize: 12.5, color: C.apagado, lineHeight: 1.45 }}>
                  {fmt(adocao.no_app)} beneficiários com conta no app, de {fmt(adocao.vidas)} vidas ativas. Cada cadastro novo faz o anel brilhar.
                </p>
              </div>
            </div>
          ) : <div style={{ fontSize: 12.5, color: C.apagado }}>O app do beneficiário não está ligado nesta instalação.<canvas ref={anelRef} width={2} height={2} style={{ display: "none" }} /></div>}
          <h2 style={{ margin: "14px 0 8px", font: `600 12px ${F.corpo}`, letterSpacing: ".12em", textTransform: "uppercase", color: C.apagado }}>Eventos por hora</h2>
          <div style={{ flex: 1, minHeight: 120, position: "relative" }}><canvas ref={curvaRef} style={{ width: "100%", height: "100%", display: "block", position: "absolute", inset: 0 }} /></div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 11.5, color: C.apagado, marginTop: 6 }}>
            <span><i style={{ display: "inline-block", width: 14, height: 2, background: C.acento, verticalAlign: "middle", marginRight: 5 }} />hoje</span>
            <span><i style={{ display: "inline-block", width: 14, height: 2, background: C.apagado, verticalAlign: "middle", marginRight: 5 }} />mesmo dia da semana passada</span>
          </div>
        </section>
      </main>

      <footer style={{ borderTop: `1px solid ${C.linha}`, fontFamily: F.dado, fontSize: 11.5, color: C.apagado, paddingTop: 8, display: "flex", flexWrap: "wrap", gap: "4px 16px" }}>
        <span>Fontes: app {estado?.fontes?.app || "…"} · URA {estado?.fontes?.telefonia || "…"} · autorizações do ERP {estado?.autorizacoes_carga?.ultima_ok ? `lidas às ${new Date(estado.autorizacoes_carga.ultima_ok).toLocaleTimeString("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit" })}` : "ainda não lidas hoje"}</span>
        {estado?.sem_cidade > 0 && <span>{fmt(estado.sem_cidade)} evento(s) de hoje sem cidade no cadastro (entram em "Outras cidades")</span>}
        <span>Atualiza sozinho: estado a cada 30 s, eventos a cada 15 s</span>
      </footer>
    </div>
  );
}
